import { Clear } from '@mui/icons-material';
import DatePicker from '@mui/lab/DatePicker';
import { Autocomplete, Box, Button, Grid, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import FormatCurrencyInput from '../../components/FormatNumber/FormatCurrencyInput';
import FormatNumberInput from '../../components/FormatNumber/FormatNumberInput';
import { typingTimeOut } from '../../constants';
import { InvoiceStatus } from '../../enums/invoiceStatus';
import { Talent } from '../../models/talent';
import { talentService } from '../../services/talentService';

export interface FilterInvoicesForm {
  talent?: Talent[];
  invoiceNumber?: string;
  company?: string;
  startDate?: string | null;
  endDate?: string | null;
  status?: string[];
  total?: string;
}

interface Props {
  handleFormSubmit: (data: FilterInvoicesForm) => void;
  filterData: FilterInvoicesForm;
}

const defaultValues: FilterInvoicesForm = {
  talent: [],
  invoiceNumber: '',
  company: '',
  startDate: null,
  endDate: null,
  status: [],
  total: ''
};

const invoicesStatusList = Object.values(InvoiceStatus).filter(
  (status) => status !== InvoiceStatus.NOT_YET_SUBMITTED
);

const FilterInvoices = (props: Props): JSX.Element => {
  const [filterData, setFilterData] = useState<FilterInvoicesForm>(
    props.filterData
  );
  const [talents, setTalents] = useState<Talent[]>([]);

  useEffect(() => {
    getTalents();
  }, []);

  const getTalents = () => {
    talentService.getTalentList().then((response) => {
      setTalents(response.data);
    });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      props.handleFormSubmit(filterData);
    }, typingTimeOut);

    return () => clearTimeout(timeoutId);
  }, [filterData]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFilterData((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value
    }));
  };

  const onClear = () => {
    setFilterData(defaultValues);
  };

  return (
    <form>
      <Grid container>
        <Grid item xs={11}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              sx={{ maxWidth: 245 }}
              label="Invoice #"
              name="invoiceNumber"
              value={filterData.invoiceNumber}
              onChange={handleChange}
              InputProps={{
                // eslint-disable-next-line
                inputComponent: FormatNumberInput as any
              }}
            />
            <Autocomplete
              multiple
              id="talent"
              options={talents}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName}`
              }
              value={filterData.talent ?? []}
              onChange={(event, newValue) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  talent: newValue
                }));
              }}
              filterSelectedOptions
              sx={{ width: '100%', maxWidth: 245 }}
              renderInput={(params) => <TextField {...params} label="Talent" />}
            />
            <TextField
              sx={{ maxWidth: 245 }}
              label="Company"
              name="company"
              value={filterData.company}
              onChange={handleChange}
            />
            <TextField
              sx={{ maxWidth: 245 }}
              label="Total Invoice"
              name="total"
              value={filterData.total}
              onChange={handleChange}
              InputProps={{
                // eslint-disable-next-line
                inputComponent: FormatCurrencyInput as any
              }}
            />
            <Autocomplete
              multiple
              id="status"
              options={invoicesStatusList}
              value={filterData.status ?? []}
              onChange={(event, newValue) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  status: newValue
                }));
              }}
              filterSelectedOptions
              sx={{ width: '100%', maxWidth: 245 }}
              renderInput={(params) => <TextField {...params} label="Status" />}
            />
            <DatePicker
              label="Start Date"
              value={filterData.startDate}
              onChange={(date) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  startDate: date
                }));
              }}
              renderInput={(params) => (
                <TextField {...params} sx={{ maxWidth: 245 }} />
              )}
            />
            <DatePicker
              label="End Date"
              value={filterData.endDate}
              onChange={(date) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  endDate: date
                }));
              }}
              renderInput={(params) => (
                <TextField {...params} sx={{ maxWidth: 245 }} />
              )}
            />
          </Box>
        </Grid>
        <Grid item xs={1}>
          <Button
            variant="text"
            startIcon={<Clear />}
            onClick={() => onClear()}
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default FilterInvoices;
